import { useContext, useState, MouseEvent } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material/';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import {
  DataTypeGlobalSelectorProps,
  FIELDS_UI_LABELS,
  FIELD_THOUSANDS_GROUPING_DEFAULT,
  getCompositeFormatKey,
  parseCompositeFormatKey,
} from '../fields/FieldsSettings/FieldsSettingsTypes';
import { ExpandMore, Settings } from '@mui/icons-material';
import { styled } from '@mui/system';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import errorResponses from '../../errorResponses';
import { getDataUIlabels } from '../../utils/getDataUIlabels';

const PopoverStyled = styled(Popover)(() => ({
  '& .MuiAccordion-root.Mui-expanded': {
    margin: '0 0 1px',
  },
  '& .MuiAccordionSummary-root': {
    height: '50px',
    minHeight: '50px',
  },
  '& .MuiCollapse-root .MuiAccordionDetails-root': {
    alignItems: 'center',
    borderTop: '1px solid #ddd',
    padding: '2px 0 4px',
    width: '168px',
  },
  '& .MuiMenuItem-root': {
    justifyContent: 'center',
    width: '100%',
  },
}));

const AccordionDetailsStyled = styled(AccordionDetails)(() => ({
  flexDirection: 'column',
  '& .active-data-type': {
    backgroundColor: '#bed9dc',
  },
}));

const DialogStyled = styled(Dialog)(() => ({
  '& .MuiDialog-paperWidthSm': {
    maxWidth: '500px',
  },
}));

const AccordionDetailsClearAll = styled(AccordionDetails)(() => ({
  cursor: 'pointer',
  justifyContent: 'center',
  paddingTop: '15px',
  '&:hover': {
    backgroundColor: '#f0f0f0',
  },
}));

const DataTypeGlobalSelector = ({
  dataTypesResponse,
  setClearFieldsFormats,
  trainingSetDataTypeConfig,
  trainingSetId,
}: DataTypeGlobalSelectorProps): JSX.Element | null => {
  const [dialogMsgDataType, setDialogMsgDataType] = useState('');
  const [dialogTitle, setDialogTitle] = useState('');
  const [formatSelectorValue, setFormatSelectorValue] = useState('');
  const [isClearFormats, setIsClearFormats] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);
  const dataTypes = [dataTypesResponse].flat();
  const glynt = useGlynt();
  const openPopover = Boolean(popoverAnchorEl);
  const popoverId = openPopover ? 'global-data-types-popover' : undefined;
  const { setToast } = useContext(AppContext);
  const queryClient = useQueryClient();

  // Get composite options for amount and number
  const amountOptions = getDataUIlabels('amount', dataTypesResponse);
  const numberOptions = getDataUIlabels('number', dataTypesResponse);

  const { mutateAsync: saveTrainingSet } = useMutation({
    mutationFn: async (params: any) => await (await glynt).trainingSet.update(params.id, params.data)
  });

  const updateTrainingSet = async (TSDataConfig: any) => {
    try {
      const dataType = dialogTitle.toLowerCase();
      let dataTypeConfig: any = {};

      if (TSDataConfig !== null) {
        switch (dataType) {
          case 'date':
            dataTypeConfig.date = {};
            dataTypeConfig.date.order = TSDataConfig;
            break;

          case 'amount': {
            // Parse composite format key
            const { decimalSymbol, thousandsGrouping } = parseCompositeFormatKey(TSDataConfig);
            dataTypeConfig.amount = { decimalSymbol, thousandsGrouping };
            break;
          }

          case 'number': {
            // Parse composite format key
            const { decimalSymbol, thousandsGrouping } = parseCompositeFormatKey(TSDataConfig);
            dataTypeConfig.number = { decimalSymbol, thousandsGrouping };
            break;
          }

          default:
            break;
        }
      } else {
        dataTypeConfig = null;
      }

      await saveTrainingSet(
        {
          id: trainingSetId,
          data: {
            data_type_config: dataTypeConfig,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ['fields']
            });
            queryClient.invalidateQueries({
              queryKey: ['trainingSet']
            });
            setToast({
              open: true,
              message: 'The global format has been successfully saved.',
              variant: 'success',
            });
          },
          onError: () => {
            setToast({
              open: true,
              message: errorResponses.failedToUpdateTrainingSetGlobalFormat,
              variant: 'error',
            });
          },
        }
      );
    } catch (error) {
      setToast({
        open: true,
        message: errorResponses.failedToUpdateTrainingSetGlobalFormat,
        variant: 'error',
      });
    }
  };

  const handleFormatsClick = (event: MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchorEl(event.currentTarget);
  };
  const handleFormatsClose = () => {
    setPopoverAnchorEl(null);
  };
  const handleShowClearFormats = () => {
    setOpenDialog(true);
    setIsClearFormats(true);
  };
  const clearAllFormats = async () => {
    await saveTrainingSet(
      {
        id: trainingSetId,
        data: {
          // TODO retrieve this information from /data-types endpoint
          // caching will be needed to avoid unnecessary requests
          data_type_config: {
            date: { order: 'MDY' },
            amount: { decimalSymbol: '.', thousandsGrouping: FIELD_THOUSANDS_GROUPING_DEFAULT },
            number: { decimalSymbol: '.', thousandsGrouping: FIELD_THOUSANDS_GROUPING_DEFAULT },
          },
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['fields']
          });
          queryClient.invalidateQueries({
            queryKey: ['trainingSet']
          });
          setClearFieldsFormats(true);
          setIsClearFormats(false);
          setToast({
            open: true,
            message: 'The Field-List Data Type Formats has been successfully cleared',
            variant: 'success',
          });
          handleFormatsClose();
        },
      }
    );
    setOpenDialog(false);
  };

  const handleDialogClickOpen = (event: any, option: string, dialogTitleProp: string) => {
    setDialogMsgDataType(event.target.innerText);
    setDialogTitle(dialogTitleProp);
    setFormatSelectorValue(option);
    setOpenDialog(true);
  };
  const handleDialogClose = () => {
    setOpenDialog(false);
  };
  const dialogClickYesHandler = () => {
    updateTrainingSet(formatSelectorValue);
    setOpenDialog(false);
    handleFormatsClose();
  };

  // Helper to get current composite key for a data type
  const getCurrentCompositeKey = (dataType: 'amount' | 'number') => {
    const config = trainingSetDataTypeConfig?.[dataType];
    return getCompositeFormatKey(
      config?.decimalSymbol || '.',
      config?.thousandsGrouping || FIELD_THOUSANDS_GROUPING_DEFAULT
    );
  };

  if (dataTypes === null || dataTypes === undefined) {
    return null;
  }

  const [dateData] = dataTypes.filter((data) => data?.name === 'date');

  return (
    <Box component="span" mr={1}>
      <Button
        aria-describedby={popoverId}
        className="mr-2"
        color="primary"
        data-cy="global-format-types-button"
        endIcon={<Settings />}
        onClick={handleFormatsClick}
        variant="contained"
      >
        FORMATS
      </Button>
      <PopoverStyled
        anchorEl={popoverAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        id={popoverId}
        onClose={handleFormatsClose}
        open={openPopover}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Accordion>
          <AccordionSummary
            aria-controls="panel1a-content"
            data-cy="global-format-date"
            expandIcon={<ExpandMore />}
            id="panel1a-header"
          >
            <Typography>Date Order</Typography>
          </AccordionSummary>
          <AccordionDetailsStyled data-cy="global-format-date-accordion-details">
            {dateData?.options?.order &&
              dateData?.options?.order.map((order: string, index: number) => {
                return (
                  <MenuItem
                    className={
                      trainingSetDataTypeConfig?.date?.order === order ? 'active-data-type' : ''
                    }
                    key={index}
                    onClick={(ev) => handleDialogClickOpen(ev, order, 'Date')}
                    value={order}
                  >
                    {FIELDS_UI_LABELS[order]}
                  </MenuItem>
                );
              })}
          </AccordionDetailsStyled>
        </Accordion>

        <Accordion>
          <AccordionSummary
            aria-controls="panel2a-content"
            data-cy="global-format-amount"
            expandIcon={<ExpandMore />}
            id="panel2a-header"
          >
            <Typography>Amount</Typography>
          </AccordionSummary>
          <AccordionDetailsStyled data-cy="global-format-amount-accordion-details">
            {Object.entries(amountOptions).map(([key, label], index) => (
              <MenuItem
                className={getCurrentCompositeKey('amount') === key ? 'active-data-type' : ''}
                key={index}
                onClick={(ev) => handleDialogClickOpen(ev, key, 'Amount')}
                value={key}
              >
                {label}
              </MenuItem>
            ))}
          </AccordionDetailsStyled>
        </Accordion>

        <Accordion>
          <AccordionSummary
            aria-controls="panel3a-content"
            data-cy="global-format-number"
            expandIcon={<ExpandMore />}
            id="panel3a-header"
          >
            <Typography>Number</Typography>
          </AccordionSummary>
          <AccordionDetailsStyled data-cy="global-format-number-accordion-details">
            {Object.entries(numberOptions).map(([key, label], index) => (
              <MenuItem
                className={getCurrentCompositeKey('number') === key ? 'active-data-type' : ''}
                key={index}
                onClick={(ev) => handleDialogClickOpen(ev, key, 'Number')}
                value={key}
              >
                {label}
              </MenuItem>
            ))}
          </AccordionDetailsStyled>
        </Accordion>

        <AccordionDetailsClearAll>
          <Box
            data-cy="global-format-clear-all"
            onClick={handleShowClearFormats}
            textAlign="center"
          >
            <Typography>Clear All Formats</Typography>
          </Box>
        </AccordionDetailsClearAll>
      </PopoverStyled>

      <DialogStyled
        aria-describedby="global-format-dialog-description"
        aria-labelledby="global-format-dialog-title"
        data-cy="global-format-dialog"
        onClose={handleDialogClose}
        open={openDialog}
      >
        <DialogTitle id="global-format-dialog-title">
          {isClearFormats ? 'Clear All Formats' : `${dialogTitle} - Confirmation`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="global-format-dialog-description">
            {isClearFormats
              ? 'This would set the field-list to the default U.S. data formats'
              : `This would set the format for all "${dialogTitle}s" on this
              field-list as "${dialogMsgDataType}". Do you want to proceed?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" data-cy="global-format-dialog-no" onClick={handleDialogClose}>
            NO
          </Button>
          <Button
            autoFocus
            color="primary"
            data-cy="global-format-dialog-yes"
            onClick={isClearFormats ? clearAllFormats : dialogClickYesHandler}
          >
            YES
          </Button>
        </DialogActions>
      </DialogStyled>
    </Box>
  );
};

export default DataTypeGlobalSelector;
